import DeleteFacility from '@Application/Facilities/Facilities/Commands/DeleteFacility';
import deleteFacility from '@Application/Facilities/Facilities/deleteFacility';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import facilityToDeleteSignal, {
	setFacilityToDelete,
} from '@UI/Pages/Agencies/Facilities/Signals/facilityToDeleteSignal';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

/**
 * The delete confirmation, mounted unconditionally alongside the agencies list. Self-contained — reads
 * the facility list already fetched by `FacilitiesContent` through a plain `useSelector`, and the
 * pending-deletion signal, rather than through props: the page has nothing of its own to pass it.
 */
const FacilitiesConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const facilities = useSelector((state) => state.facilitiesStore.facilities);
	const facilityToDelete = useSignalValue(facilityToDeleteSignal);

	const facilityBeingDeleted = React.useMemo(
		() => (facilityToDelete !== null ? (facilities?.findById(facilityToDelete) ?? null) : null),
		[facilities, facilityToDelete],
	);

	const handleDeleteDialogHide = React.useCallback(() => {
		setFacilityToDelete(null);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (organizationId === null || facilityToDelete === null) {
			return;
		}

		dispatch(deleteFacility(new DeleteFacility(organizationId, facilityToDelete))).catch(
			ignoreHandledError,
		);
		// Close immediately: `acceptCallback` never triggers `onHide`, and waiting for the
		// request to settle would leave the dialog open long enough for a second click.
		setFacilityToDelete(null);
	}, [organizationId, facilityToDelete, dispatch]);

	return (
		<ConfirmDeleteDialog
			visible={facilityToDelete !== null}
			onHide={handleDeleteDialogHide}
			header="Supprimer l’agence"
			message={
				<React.Fragment>
					Cette action est irréversible. Confirmer la suppression de l’agence{' '}
					<strong>{facilityBeingDeleted?.name ?? ''}</strong> ?
				</React.Fragment>
			}
			confirmText={facilityBeingDeleted?.name ?? ''}
			acceptCallback={handleDeleteAccept}
		/>
	);
};

export default FacilitiesConfirmationDialogs;
